import { FC, memo, useMemo, type ReactNode } from 'react';
import { isEmpty, isNil } from 'lodash';
// Import Types
import type { PartnerSettingsPermissionsKeys } from 'types';
// Import Hooks
import useHasPermission from 'hooks/UseHasPermission';
// Import Enums
import { PermissionEnum } from 'enums';

/**
 * @interface Props
 * @description Props for the AccessControl component.
 */
interface Props {
    /**
     * The content to be rendered if access is granted.
     * @type {ReactNode}
     */
    children: ReactNode;
    /**
     * A single permission key (PermissionEnum) or an array of permission keys (PermissionEnum[]) to check.
     * Access is granted if the user has **at least one** of these permissions.
     * If this is the only prop provided, the component will display if the user has any one of the listed permissions.
     * @type {PermissionEnum | PermissionEnum[]}
     * @optional
     */
    permissions?: PermissionEnum | PermissionEnum[];
    /**
     * An array of permission keys (PermissionEnum[]) that are **all** required for access.
     * Access is granted only if the user has **all** of these permissions.
     * @type {PermissionEnum[]}
     * @default []
     * @optional
     */
    mandatoryPermissions?: PermissionEnum[];
    /**
     * A specific partner setting key to check.
     * Access is granted only if the partner has this setting enabled.
     * @type {PartnerSettingsPermissionsKeys}
     * @optional
     */
    partnerSetting?: PartnerSettingsPermissionsKeys;
}
const AccessControl: FC<Props> = ({ children, permissions = '', mandatoryPermissions = [], partnerSetting }) => {
    const { hasPermission, hasPartnerSettings } = useHasPermission();

    const hasAccess = useMemo(() => {
        // 1. Check Partner Setting
        if (!isNil(partnerSetting) && !hasPartnerSettings(partnerSetting)) {
            return false;
        }

        // 2. Check Mandatory Permissions (Requires ALL to be present)
        if (!isEmpty(mandatoryPermissions) && !hasPermission(mandatoryPermissions, true)) {
            return false;
        }

        // 3. Check General Permissions (Requires AT LEAST ONE to be present)
        if (!isEmpty(permissions) && !hasPermission(permissions)) {
            return false;
        }

        // If all checks pass or no checks were specified, grant access
        return true;
    }, [permissions, mandatoryPermissions, partnerSetting, hasPermission, hasPartnerSettings]);

    return hasAccess ? <>{children}</> : null;
};

/**
 * @component
 * @description A higher-order component for controlling access to content based on user permissions and/or partner settings.
 *
 * The component evaluates access based on the following hierarchy:
 * 1. Checks `partnerSetting`: If provided and the partner does not have the setting, access is denied.
 * 2. Checks `mandatoryPermissions`: If provided and the user does not have **all** of them, access is denied.
 * 3. Checks `permissions`: If provided and the user does not have **at least one** of them, access is denied.
 *
 * If none of the access conditions deny access (or if no access props are provided), the content is rendered.
 *
 * @param {Props} props The component props.
 * @returns {ReactNode | null} The children if access is granted, otherwise `null`.
 */
export default memo(AccessControl);
